import { useLang } from "../i18n";
import { fmtNum } from "../api";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export const TradesTable = ({ trades, title }) => {
  const { t } = useLang();
  return (
    <div data-testid="trades-table" className="border border-white/10 bg-[#111111]">
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
        <h3 className="text-sm font-medium text-slate-200">{title}</h3>
        <span className="text-xs font-mono text-slate-500">{trades.length} ops</span>
      </div>
      <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
        <table className="w-full text-xs font-mono">
          <thead className="sticky top-0 bg-[#1A1A1A]">
            <tr className="text-slate-500 uppercase tracking-wider text-[10px]">
              <th className="text-left px-4 py-2">{t("col_contract")}</th>
              <th className="text-left px-2 py-2">{t("col_dir")}</th>
              <th className="text-right px-2 py-2">{t("col_open")}</th>
              <th className="text-right px-2 py-2">{t("col_open_price")}</th>
              <th className="text-right px-2 py-2">{t("col_close")}</th>
              <th className="text-right px-2 py-2">{t("col_close_price")}</th>
              <th className="text-right px-2 py-2">{t("col_pl")}</th>
              <th className="text-right px-4 py-2">{t("col_status")}</th>
            </tr>
          </thead>
          <tbody>
            {trades.map((tr) => {
              const pl = tr.status === "open" ? tr.open_pl_eur : tr.pl_eur;
              return (
                <tr key={tr.id} data-testid={`trade-row-${tr.id}`} className="border-t border-white/5 hover:bg-white/[0.03]">
                  <td className="px-4 py-2 text-slate-300">{tr.contract}{tr.rollover ? ` · ${t("rollover_label")}` : ""}{tr.closed_by === "tp" ? " · TP" : ""}</td>
                  <td className={`px-2 py-2 font-bold ${tr.direction === "LONG" ? "text-emerald-400" : "text-red-400"}`}>{tr.direction}</td>
                  <td className="px-2 py-2 text-right text-slate-400">{tr.open_date}</td>
                  <td className="px-2 py-2 text-right text-slate-300">{fmtNum(tr.open_price)}</td>
                  <td className="px-2 py-2 text-right text-slate-400">{tr.close_date || "—"}</td>
                  <td className="px-2 py-2 text-right text-slate-300">{tr.close_price != null ? fmtNum(tr.close_price) : "—"}</td>
                  <td className={`px-2 py-2 text-right ${pl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {pl != null ? `${pl > 0 ? "+" : ""}${Math.round(pl).toLocaleString()}` : "—"}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <span className={`text-[10px] px-1.5 py-0.5 border ${tr.status === "open" ? "border-amber-500/40 text-amber-400" : "border-white/10 text-slate-500"}`}>
                      {tr.status === "open" ? t("open_label") : t("closed_label")}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const EquityChart = ({ data, title }) => {
  const { t } = useLang();
  const positive = data.length && data[data.length - 1].equity >= 0;
  const color = positive ? "#10B981" : "#EF4444";
  return (
    <div data-testid="equity-chart" className="border border-white/10 bg-[#111111] p-4">
      <h3 className="text-sm font-medium text-slate-200 mb-4">{title || t("equity_title")}</h3>
      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="eq" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.25} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#262626" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "IBM Plex Mono" }} minTickGap={50} />
          <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "IBM Plex Mono" }} width={60}
            tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
          <Tooltip contentStyle={{ background: "#1A1A1A", border: "1px solid #262626", fontSize: 12, fontFamily: "IBM Plex Mono" }}
            labelStyle={{ color: "#94A3B8" }} formatter={(v) => [`${Math.round(v).toLocaleString()} €`, "Equity"]} />
          <Area type="monotone" dataKey="equity" stroke={color} strokeWidth={1.5} fill="url(#eq)" isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};
